import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AdminAuthProvider } from './contexts/AdminAuthContext';
import { MentorAuthProvider, useMentorAuth } from './contexts/MentorAuthContext';
import { ChatProvider } from './contexts/ChatContext';
import ProtectedRoute from './components/ProtectedRoute';
import ProtectedAdminRoute from './components/ProtectedAdminRoute';
import ProtectedMentorRoute from './components/ProtectedMentorRoute';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import FeaturedIn from './components/FeaturedIn';
import Philosophy from './components/Philosophy';
import Curriculum from './components/Curriculum';
import Certification from './components/Certification';
import Mentors from './components/Mentors';
import Community from './components/Community';
import Comparison from './components/Comparison';
import Admissions from './components/Admissions';
import Testimonials from './components/Testimonials';
import Manifesto from './components/Manifesto';
import FAQ from './components/FAQ';
import Footer from './components/Footer';
import SectionDivider from './components/SectionDivider';
import Dashboard from './pages/Dashboard';
import ViewProfile from './pages/ViewProfile';
import AuthenticationSettings from './pages/AuthenticationSettings';
import StudentChatPage from './pages/StudentChatPage';
import AdminLoginPage from './pages/AdminLoginPage';
import AdminLayout from './components/AdminLayout';
import AdminDashboard from './pages/AdminDashboard';
import UsersManagement from './pages/UsersManagement';
import ScholarshipsManagement from './pages/ScholarshipsManagement';
import PaymentsManagement from './pages/PaymentsManagement';
import SessionsManagement from './pages/SessionsManagement';
import AuditLogViewer from './pages/AuditLogViewer';
import WebhooksMonitor from './pages/WebhooksMonitor';
import UserDetailPage from './pages/UserDetailPage';
import CohortsManagement from './pages/CohortsManagement';
import CohortDetailPage from './pages/CohortDetailPage';
import MentorsManagement from './pages/MentorsManagement';
import MentorDetailPage from './pages/MentorDetailPage';
import MentorLoginPage from './pages/MentorLoginPage';
import MentorDashboard from './pages/MentorDashboard';
import MentorProfile from './pages/MentorProfile';
import MentorSessions from './pages/MentorSessions';
import MentorStats from './pages/MentorStats';
import MentorSecurity from './pages/MentorSecurity';
import MentorChatPage from './pages/MentorChatPage';
import MentorLayout from './components/MentorLayout';
import FloatingWhatsAppButton from './components/FloatingWhatsAppButton';
import StickyScholarshipButton from './components/StickyScholarshipButton';
import ScholarshipCTA from './components/ScholarshipCTA';
import './App.css';

// Configurar React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutos
    },
  },
});

const Home = () => {
  React.useEffect(() => {
    // Scroll to section if hash exists in URL
    const hash = window.location.hash;
    if (hash) {
      // Small delay to ensure the page is fully loaded
      setTimeout(() => {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
    }
  }, []);

  return (
    <div className="min-h-screen bg-black">
      <Navbar />
      {/* MANIFIESTO AL INICIO - Optimizado para Meta Ads */}
      <Manifesto />
      <SectionDivider variant="gradient" />
      {/* CTA #1 - Después del Manifiesto (emocional) */}
      <ScholarshipCTA variant="manifesto" />
      <SectionDivider variant="particles" />
      <Philosophy />
      <SectionDivider variant="particles" />
      <Hero />
      <SectionDivider variant="wave" />
      <FeaturedIn />
      <SectionDivider variant="line" />
      <Curriculum />
      {/* CTA #2 - Después del Curriculum (práctico) */}
      <ScholarshipCTA 
        variant="curriculum"
        title="Este programa puede ser tuyo"
        subtitle="Aplica a la Beca Libertad y accede a formación de élite sin barreras económicas."
      />
      <SectionDivider variant="gradient" />
      <Certification />
      <SectionDivider variant="wave" />
      <Mentors />
      <SectionDivider variant="pulse" />
      <Community />
      <SectionDivider variant="line" />
      <Comparison />
      <SectionDivider variant="gradient" />
      <Admissions />
      <SectionDivider variant="particles" />
      <Testimonials />
      <SectionDivider variant="line" />
      <FAQ />
      <SectionDivider variant="gradient" />
      <Footer />
      {/* Sticky Button - Siempre visible después de scroll */}
      <StickyScholarshipButton />
    </div>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AdminAuthProvider>
          <MentorAuthProvider>
            <ChatProviderWrapper />
          </MentorAuthProvider>
        </AdminAuthProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

// ChatProviderWrapper Component - inside providers
const ChatProviderWrapper = () => {
  const { user, getToken, isAuthenticated } = useAuth();
  const { mentor, getToken: getMentorToken } = useMentorAuth();
  
  // Get token reactively
  const [token, setToken] = React.useState(null);
  const [userId, setUserId] = React.useState(null);
  
  React.useEffect(() => {
    // Update token when auth state changes
    const currentToken = getToken() || (getMentorToken ? getMentorToken() : null);
    const currentUserId = user?.id || mentor?.id;
    
    setToken(currentToken);
    setUserId(currentUserId);
  }, [user, mentor, getToken, getMentorToken, isAuthenticated]);
  
  return (
    <ChatProvider token={token} userId={userId}>
      <div className="App">
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Home />} />
            
            {/* User Dashboard Routes (Protected) */}
            <Route element={<ProtectedRoute />}>
              <Route path="/app" element={<Dashboard />} />
              <Route path="/app/profile" element={<ViewProfile />} />
              <Route path="/app/authentication" element={<AuthenticationSettings />} />
              <Route path="/app/chat" element={<StudentChatPage />} />
            </Route>

            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
            <Route element={<ProtectedAdminRoute><AdminLayout /></ProtectedAdminRoute>}>
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/admin/users" element={<UsersManagement />} />
              <Route path="/admin/users/:userId" element={<UserDetailPage />} />
              <Route path="/admin/cohorts" element={<CohortsManagement />} />
              <Route path="/admin/cohorts/:cohortId" element={<CohortDetailPage />} />
              <Route path="/admin/mentors" element={<MentorsManagement />} />
              <Route path="/admin/mentors/:mentorId" element={<MentorDetailPage />} />
              <Route path="/admin/scholarships" element={<ScholarshipsManagement />} />
              <Route path="/admin/payments" element={<PaymentsManagement />} />
              <Route path="/admin/sessions" element={<SessionsManagement />} />
              <Route path="/admin/webhooks" element={<WebhooksMonitor />} />
              <Route path="/admin/audit" element={<AuditLogViewer />} />
            </Route>

            {/* Mentor Routes */}
            <Route path="/mentor/login" element={<MentorLoginPage />} />
            <Route element={<ProtectedMentorRoute><MentorLayout /></ProtectedMentorRoute>}>
              <Route path="/mentor/dashboard" element={<MentorDashboard />} />
              <Route path="/mentor/profile" element={<MentorProfile />} />
              <Route path="/mentor/sessions" element={<MentorSessions />} />
              <Route path="/mentor/stats" element={<MentorStats />} />
              <Route path="/mentor/security" element={<MentorSecurity />} />
              <Route path="/mentor/chat" element={<MentorChatPage />} />
            </Route>
          </Routes>
          
          {/* Floating WhatsApp Button */}
          <FloatingWhatsAppButton />
        </BrowserRouter>
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={true}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="dark"
        />
      </div>
    </ChatProvider>
  );
};

export default App;
